import { useState, useRef, useEffect } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useSidebar } from '../../contexts/SidebarContext'
import { useAuth } from '../../hooks/useAuth'
import { type Profile } from '../../types'
import { getProfileUrl } from '../../utils/url'
import Logo from '../common/Logo'
import Tooltip from '../common/Tooltip'

interface SidebarProps {
  profile: Profile | null
  isOpen?: boolean
  onClose?: () => void
}

const navItems = [
  {
    name: 'Home',
    path: '/home',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
        />
      </svg>
    ),
  },
  {
    name: 'Edit Details',
    path: '/edit',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
        />
      </svg>
    ),
  },
  {
    name: 'Customize',
    path: '/customize',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
        />
      </svg>
    ),
  },
  {
    name: 'Blog',
    path: '/blog',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
        />
      </svg>
    ),
  },
  {
    name: 'Pages',
    path: '/pages',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    ),
  },
  {
    name: 'Account',
    path: '/account',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
        />
      </svg>
    ),
  },
]

export default function Sidebar({ profile, isOpen, onClose }: SidebarProps) {
  const { user, logout } = useAuth()
  const { isCollapsed, toggleCollapse } = useSidebar()
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Close user menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
    }

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [userMenuOpen])

  const handleSignOut = async () => {
    setUserMenuOpen(false)
    await logout()
    navigate('/')
  }

  // Avatar component to avoid repetition
  const Avatar = ({ size = 'md' }: { size?: 'sm' | 'md' }) => {
    const sizeClass = size === 'sm' ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm'
    return profile?.profile_image_url ? (
      <img
        src={profile.profile_image_url}
        alt={profile.full_name}
        className={`${sizeClass} rounded-full object-cover`}
      />
    ) : (
      <div
        className={`${sizeClass} bg-black rounded-full flex items-center justify-center text-white font-bold`}
      >
        {profile?.first_name?.[0] || user?.email?.[0]?.toUpperCase()}
        {profile?.last_name?.[0] || ''}
      </div>
    )
  }

  // Mobile: Slide-in drawer
  if (typeof isOpen !== 'undefined') {
    return (
      <>
        {/* Backdrop */}
        {isOpen && (
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
            onClick={onClose}
          />
        )}

        {/* Slide-in sidebar */}
        <aside
          className={`fixed top-0 left-0 h-full w-72 bg-white/90 backdrop-blur-xl border-r border-black/10 z-50 transform transition-transform duration-300 ease-out lg:hidden ${
            isOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          {/* Header with logo and close button */}
          <div className="flex items-center justify-between p-4 border-b border-black/10">
            <Logo size="lg" to="/home" onClick={onClose} />
            <button
              onClick={onClose}
              className="p-2 text-black/40 hover:text-black/60 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="flex flex-col h-[calc(100%-65px)]">
            {/* Navigation */}
            <nav className="flex-1 px-2 py-4">
              <ul className="space-y-1">
                {navItems.map((item) => (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      onClick={onClose}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all ${
                          isActive
                            ? 'bg-black/10 text-black font-semibold'
                            : 'text-black/70 hover:bg-black/5 hover:text-black'
                        }`
                      }
                    >
                      {item.icon}
                      <span className="font-medium">{item.name}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>

              {/* View Profile Button */}
              {profile?.username && (
                <div className="mt-4 px-2">
                  <a
                    href={getProfileUrl(profile.username)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={onClose}
                    className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-gray-800 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-all"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                    <span>View Profile</span>
                  </a>
                </div>
              )}
            </nav>

            {/* User Section */}
            {(profile || user) && (
              <div className="border-t border-black/10 p-4 space-y-3">
                {/* User Info with Sign Out */}
                <div className="flex items-center gap-3">
                  <Avatar />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-black truncate">
                      {profile?.full_name || 'User'}
                    </p>
                    <p className="text-sm text-black/60 truncate">{user?.email}</p>
                  </div>
                </div>

                {/* Sign Out Button */}
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-all"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  <span>Sign out</span>
                </button>
              </div>
            )}
          </div>
        </aside>
      </>
    )
  }

  // Desktop: Fixed sidebar with collapse support
  return (
    <aside
      className={`hidden lg:flex lg:flex-col fixed left-0 top-0 h-screen bg-white/70 backdrop-blur-xl border-r border-black/10 transition-all duration-300 ease-in-out ${
        isCollapsed ? 'w-16' : 'w-64'
      }`}
    >
      {/* Collapse toggle button */}
      <button
        onClick={toggleCollapse}
        className="absolute -right-3 top-6 w-6 h-6 bg-white border border-black/10 rounded-full shadow-md flex items-center justify-center hover:bg-black/5 transition-colors z-10"
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <svg
          className={`w-4 h-4 text-black/60 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      {/* Logo */}
      <div className={`p-4 border-b border-black/10 ${isCollapsed ? 'flex justify-center' : ''}`}>
        <Logo size="lg" to="/home" iconOnly={isCollapsed} />
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-4">
        <ul className="space-y-1">
          {navItems.map((item) => (
            <li key={item.path}>
              <Tooltip content={item.name} disabled={!isCollapsed}>
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center ${isCollapsed ? 'justify-center' : 'gap-2.5'} px-3 py-2 rounded-lg text-sm transition-all ${
                      isActive
                        ? 'bg-black/10 text-black font-semibold'
                        : 'text-black/70 hover:bg-black/5 hover:text-black'
                    }`
                  }
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  {!isCollapsed && <span className="font-medium">{item.name}</span>}
                </NavLink>
              </Tooltip>
            </li>
          ))}
        </ul>

        {/* View Profile Button */}
        {profile?.username && (
          <div className={`mt-4 ${isCollapsed ? 'px-1' : 'px-2'}`}>
            <Tooltip content="View Profile" disabled={!isCollapsed}>
              <a
                href={getProfileUrl(profile.username)}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-gray-800 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-all ${isCollapsed ? 'px-2' : ''}`}
              >
                <svg
                  className="w-4 h-4 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
                {!isCollapsed && <span>View Profile</span>}
              </a>
            </Tooltip>
          </div>
        )}
      </nav>

      {/* User Section */}
      {(profile || user) && (
        <div className={`border-t border-black/10 p-4 ${isCollapsed ? '' : 'space-y-3'}`}>
          {/* User Info with Dropdown */}
          <div ref={userMenuRef} className={`relative ${isCollapsed ? 'flex justify-center' : ''}`}>
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className={`${isCollapsed ? '' : 'w-full'} flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} hover:bg-black/5 rounded-lg p-2 transition-all`}
            >
              <Avatar />
              {!isCollapsed && (
                <div className="flex-1 min-w-0 text-left">
                  <p className="font-medium text-black truncate">{profile?.full_name || 'User'}</p>
                  <p className="text-sm text-black/60 truncate">{user?.email}</p>
                </div>
              )}
            </button>

            {/* Dropdown Menu */}
            {userMenuOpen && (
              <div
                className={`absolute ${isCollapsed ? 'left-full ml-2 bottom-0' : 'bottom-full left-0 mb-2 w-full'} bg-white/95 backdrop-blur-md border border-black/10 rounded-lg shadow-lg overflow-hidden z-50`}
              >
                {isCollapsed && (
                  <div className="px-3 py-2 border-b border-black/10">
                    <p className="font-medium text-black text-sm truncate">
                      {profile?.full_name || 'User'}
                    </p>
                    <p className="text-xs text-black/60 truncate">{user?.email}</p>
                  </div>
                )}
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 transition-all"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  <span>Sign out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </aside>
  )
}
